import { apiGet, apiPost, buildHeaders } from '../../../shared/api-client/index.js';

export interface ImportPreview {
  headers: string[];
  rowsPreview: string[][];
  totalRows: number;
  rawRows: string[][];
  /** true, когда строк в файле больше лимита импорта и часть отброшена. */
  truncated?: boolean;
}

export interface ImportJob {
  id: string;
  workspaceId: string;
  entityType: string;
  status: string;
  fileName: string;
  preview: ImportPreview | null;
  mapping: Record<string, string> | null;
  result: {
    created: number;
    skipped: number;
    failed: number;
    errors: Array<{ row: number; message: string }>;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export async function createImportJob(
  entityType: string,
  file: File,
): Promise<{
  id: string;
  preview: ImportPreview;
  mapping: Record<string, string>;
  updatedAt: string;
}> {
  const form = new FormData();
  form.append('entityType', entityType);
  form.append('file', file);
  const headers = buildHeaders();
  // Let browser set Content-Type for FormData
  delete (headers as Record<string, string>)['Content-Type'];
  const res = await fetch(`/api/v1/imports`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error ?? `Upload failed: ${res.status}`);
  }
  return res.json();
}

export async function getImportJob(id: string): Promise<{ job: ImportJob }> {
  return apiGet<{ job: ImportJob }>(`/imports/${id}`);
}

export async function updateImportMapping(
  id: string,
  mapping: Record<string, string>,
  expectedUpdatedAt: string,
): Promise<{ id: string; mapping: Record<string, string>; updatedAt: string }> {
  return apiPost<{ id: string; mapping: Record<string, string>; updatedAt: string }>(
    `/imports/${id}/mapping`,
    { mapping, expectedUpdatedAt },
  );
}

export async function executeImport(
  id: string,
  expectedUpdatedAt: string,
): Promise<{
  created: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
}> {
  return apiPost(`/imports/${id}/execute`, { expectedUpdatedAt });
}

export async function exportEntities(entityType: string): Promise<void> {
  const headers = buildHeaders();
  const res = await fetch(`/api/v1/exports/${entityType}`, {
    headers,
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entityType}-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
